import { useEffect, useState } from 'react';
import {
  makeStyles,
  tokens,
  Field,
  Input,
  Dropdown,
  Option,
  Combobox,
  Persona,
  Spinner,
  Text,
} from '@fluentui/react-components';
import { EmployeeUpdateFormData, GraphUser } from '../../../hooks/useEmployeeUpdateForm';
import { apiClient, ListResponse, searchUsers } from '../../../api/client';
import { EMPLOYMENT_TYPES } from '../../../config/constants';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: tokens.spacingHorizontalL,
  },
  fullWidth: {
    gridColumn: '1 / -1',
  },
  section: {
    marginBottom: tokens.spacingVerticalXL,
  },
  sectionTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    marginBottom: tokens.spacingVerticalM,
    color: tokens.colorNeutralForeground1,
  },
  required: {
    color: tokens.colorPaletteRedForeground1,
  },
});

interface Site {
  _id: string;
  SITE_CODE: string;
  Company_Name: string;
  City: string;
  Country: string;
}

interface EmployeeDetailsStepProps {
  formData: EmployeeUpdateFormData;
  updateFormData: (updates: Partial<EmployeeUpdateFormData>) => void;
}

export function EmployeeDetailsStep({ formData, updateFormData }: EmployeeDetailsStepProps) {
  const styles = useStyles();
  const [sites, setSites] = useState<Site[]>([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [managerQuery, setManagerQuery] = useState(formData.managerName || '');
  const [managers, setManagers] = useState<GraphUser[]>([]);
  const [managersLoading, setManagersLoading] = useState(false);

  // Load sites
  useEffect(() => {
    apiClient
      .get<ListResponse<Site>>('/sites')
      .then((response) => setSites(response.data))
      .catch(console.error)
      .finally(() => setSitesLoading(false));
  }, []);

  // Search managers with debounce
  useEffect(() => {
    if (managerQuery.length < 2) {
      setManagers([]);
      return;
    }

    const timer = setTimeout(async () => {
      setManagersLoading(true);
      try {
        const results = await searchUsers(managerQuery);
        setManagers(results);
      } catch (error) {
        console.error('Error searching managers:', error);
      } finally {
        setManagersLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [managerQuery]);

  return (
    <div className={styles.container}>
      <Text size={500} weight="semibold">
        Employee Details
      </Text>
      <Text>Update employee information. All fields are optional except Manager.</Text>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>Basic Information</div>
        <div className={styles.grid}>
          <Field label="Job Title">
            <Input
              value={formData.jobTitle}
              onChange={(_, data) => updateFormData({ jobTitle: data.value })}
              placeholder="e.g., Software Engineer"
            />
          </Field>

          <Field label="Department">
            <Input
              value={formData.department}
              onChange={(_, data) => updateFormData({ department: data.value })}
              placeholder="e.g., Engineering"
            />
          </Field>

          <Field label="Mobile Phone">
            <Input
              value={formData.mobilePhone}
              onChange={(_, data) => updateFormData({ mobilePhone: data.value })}
              placeholder="+1-555-0123"
              type="tel"
            />
          </Field>

          <Field label="Business Phone">
            <Input
              value={formData.businessPhone}
              onChange={(_, data) => updateFormData({ businessPhone: data.value })}
              placeholder="+1-555-0456"
              type="tel"
            />
          </Field>

          <Field label="Employee ID">
            <Input
              value={formData.employeeId}
              onChange={(_, data) => updateFormData({ employeeId: data.value })}
              placeholder="EMP-12345"
            />
          </Field>

          <Field label="Employee Type">
            <Dropdown
              value={formData.employmentType}
              selectedOptions={[formData.employmentType]}
              onOptionSelect={(_, data) =>
                updateFormData({ employmentType: data.optionValue as 'Employee' | 'Contractor' })
              }
            >
              {EMPLOYMENT_TYPES.map((type) => (
                <Option key={type.key} value={type.key} text={type.text}>
                  {type.text}
                </Option>
              ))}
            </Dropdown>
          </Field>
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>
          Manager <span className={styles.required}>*</span>
        </div>
        <Field
          label="Line Manager"
          required
          validationMessage={!formData.managerId ? 'Manager is required' : undefined}
          validationState={!formData.managerId ? 'error' : 'none'}
        >
          <Combobox
            placeholder="Search for manager..."
            value={managerQuery}
            onInput={(e) => setManagerQuery((e.target as HTMLInputElement).value)}
            onOptionSelect={(_, data) => {
              const selectedManager = managers.find((m) => m.id === data.optionValue);
              if (selectedManager) {
                updateFormData({
                  managerId: selectedManager.id,
                  managerName: selectedManager.displayName,
                  managerEmail: selectedManager.mail || selectedManager.userPrincipalName,
                });
                setManagerQuery(selectedManager.displayName);
              }
            }}
            selectedOptions={formData.managerId ? [formData.managerId] : []}
          >
            {managersLoading && (
              <Option key="loading" disabled text="Loading...">
                <Spinner size="tiny" label="Loading..." />
              </Option>
            )}
            {!managersLoading &&
              managers.map((manager) => (
                <Option key={manager.id} value={manager.id} text={manager.displayName}>
                  <Persona
                    name={manager.displayName}
                    secondaryText={manager.mail || manager.userPrincipalName}
                    size="small"
                  />
                </Option>
              ))}
            {!managersLoading && managers.length === 0 && managerQuery.length >= 2 && (
              <Option key="no-results" disabled text="No managers found">
                No managers found
              </Option>
            )}
          </Combobox>
        </Field>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>Location</div>
        <Field label="Site">
          <Dropdown
            placeholder={sitesLoading ? 'Loading sites...' : 'Select site'}
            value={formData.siteCode}
            selectedOptions={formData.siteCode ? [formData.siteCode] : []}
            onOptionSelect={(_, data) => updateFormData({ siteCode: data.optionValue || '' })}
            disabled={sitesLoading}
          >
            {sites.map((site) => (
              <Option key={site._id} value={site.SITE_CODE} text={`${site.SITE_CODE} - ${site.City}, ${site.Country}`}>
                {site.SITE_CODE} - {site.City}, {site.Country}
              </Option>
            ))}
          </Dropdown>
        </Field>
      </div>
    </div>
  );
}
